// ============================================================================
// ui/credit-image.js — the Blue Sky footer on the downloaded PNG
// ============================================================================
// WHY THIS EXISTS. Every other way a design leaves the tool carries a link back:
// Copy link, Share via…, Email my design. The DOWNLOAD is the one artifact that
// travels with no return path — it lands in a booster-club email, an AD's slide
// deck, a group text, and nobody looking at it knows where it came from or how to
// get one. This composes the plate onto a small sheet with the Blue Sky mark and
// the domain under it, so the image is its own way back.
//
// ⛔ IT IS APPLIED AT THE DOWNLOAD CALL SITE, NOT INSIDE `snapshotPNG`.
// Four other things pull the same snapshot and every one of them must stay clean:
//   • the rep design-sheet PDF (`/wd-pdf` composes its own letterhead)
//   • the quote-saved modal's thumbnail (`openQuoteSavedModal`)
//   • the R2 showcase batch (`plate-batch.js` — those become site imagery)
//   • the mobile card picker (`card-preview.js`)
// Move this into the snapshot and all four silently grow a footer.
//
// WHY THE IMAGE GAINS A BACKGROUND. `portraitPNG` returns a TRANSPARENT PNG
// cropped to the product's own alpha bounds — so there is no surface for a footer
// to sit on and no guarantee about what shows through behind it. The sheet is the
// footer's substrate, not a decoration: on a transparent canvas the mark would
// vanish against any dark viewer, which is the same failure as a light-on-light
// logo, arrived at from the other side.
//
// ⚠ WHICH MARK — THE PRIMARY (DARK INK) ONE, and it is picked from the SURFACE IT
// LANDS ON rather than from the app around it. The stage behind the designer is
// GD-dark, so the instinct is the white mark; the sheet this draws is WHITE, and
// the white mark would disappear into it. Exactly the mistake `.wd-pdb__logo` cost
// a day of (WD 220, `ui/product-info.js`) — the URL is imported from that record
// rather than copied, so the two cannot drift.
//
// CROSS-ORIGIN. The Shopify CDN answers with `access-control-allow-origin: *`
// (verified 2026-08-03), so `crossOrigin = 'anonymous'` lets the mark be drawn
// without tainting the canvas. If that ever stops being true, `toBlob` throws a
// SecurityError — which is caught, and the buyer gets the bare plate rather than
// no download at all. Every failure in here degrades to the un-footered image.
// ============================================================================

import { BRANDS } from './product-info.js';

/** The Brand-Kit "dark logo, for light backgrounds" mark. One source, in BRANDS. */
const LOGO_URL = BRANDS.bluesky.logo;

/** The line under the mark. The mark already says the NAME — this is the way back. */
const SITE_LINE = 'blueskyfitsupply.com';

/* ── geometry ────────────────────────────────────────────────────────────────
   Every number is a SHARE, never a pixel count, because one constant set has to
   serve both sizes `portraitPNG` produces: 1536 on a phone and 3072 on a desktop.
   A footer sized in pixels would be a band on one and a hairline on the other. */
const PAD_OF_LONG_EDGE = 0.055;  // white margin around the plate

/* ── the footer: THE WEBSITE, and nothing else (Ryan 2026-08-07) ──────────────
   *"remove the logo in the footer. just keep the website. Make sure the website is big enough to be
   readable on both — a little small on the single page."*
   The mark moved to the HEADER, so repeating it below was the brand twice on one small sheet. With
   the band now holding one line instead of a stacked pair it can be shallower AND the type larger —
   the old 0.0135 was sized to leave room for a logo above it, which is why it read small. */
const FOOTER_OF_WIDTH = 0.075;
const FOOTER_MIN_PX = 78;
const TEXT_OF_WIDTH = 0.024;     // the domain line's size — ~1.8x the old one
const TEXT_MIN_PX = 22;

/* ── the header: THE MARK (Ryan 2026-08-07) ──────────────────────────────────
   *"remove 'Blue Sky Fitness Supply' and replace it with the logo. Remove the sub-header on both."*
   A wordmark says the name better than the words do, and the sub-header was carrying different
   information on each artefact ("Custom Weight Renderings" vs the product name) — which made the two
   files look like different documents rather than one set. */
const HEAD_LOGO_OF_WIDTH = 0.34;
const HEAD_LOGO_MAX_H_OF_WIDTH = 0.085; // a wide artefact must not get a giant mark
/* ⚠ TOP AND BOTTOM ARE SEPARATE (Ryan 2026-08-07: *"decrease gap below logo and first image on
   all"*). They were one number, which looked balanced in isolation and was wrong in place: the space
   under the mark is not the only thing between it and the artwork — `PAD_OF_LONG_EDGE` adds the
   plate's own top margin directly beneath it, so a symmetric header pad renders as roughly double
   the gap below what it is above. Splitting them lets the header sit tight to the image while
   keeping real air above the mark. */
const HEAD_PAD_TOP_OF_LOGO = 0.55;
const HEAD_PAD_BOTTOM_OF_LOGO = 0.14;

/* ── the header block (Ryan 2026-08-07) ──────────────────────────────────────
   *"the individual downloaded images should have a header of 'Blue Sky Fitness Supply' and the
   sub-header of whatever the product name is."* Shares are used here for the same reason the footer
   uses them: one constant set serves a 1536px phone portrait and a 3072px desktop one, and a header
   sized in pixels would be a banner on one and a whisper on the other. */
const TITLE_OF_WIDTH = 0.030;
const TITLE_MIN_PX = 26;
const SUBTITLE_OF_TITLE = 0.62;   // the sub-header, relative to the header
const HEADER_GAP_OF_TITLE = 0.42; // between the two lines
const HEADER_PAD_OF_TITLE = 0.85; // above the header and below the sub-header
const TITLE_INK = '#000F1C';

/* ⚠ NO DIVIDING RULE. There was a hairline between the plate and the footer and Ryan cut it
   (2026-08-03): a line is what makes two things read as two panels, so the white above it and the
   white below it stopped being one field and the gap looked wider than it measured. Nothing about
   the geometry changed when it went — the band is still `FOOTER_OF_WIDTH` — which is the point
   worth keeping: the gap was never a spacing problem. Don't reintroduce it to "separate" the
   footer; the whitespace already does that. */
const SHEET = '#FFFFFF';
const TEXT_INK = 'rgba(0, 15, 28, 0.66)';    // the brand navy, as the domain line
const UI_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * The mark, loaded once per session.
 *
 * Cached as the PROMISE rather than the image, so two fast downloads join one
 * fetch instead of racing two. Resolves to `null` on any failure — a missing mark
 * is a footer with just the domain line on it, not a broken download.
 */
let logoPromise = null;
function loadLogo() {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img.naturalWidth > 0 ? img : null);
      img.onerror = () => resolve(null);
      img.src = LOGO_URL;
    } catch { resolve(null); }
  });
  return logoPromise;
}

/** A blob → an decoded HTMLImageElement, or null. */
function loadBlob(blob) {
  return new Promise((resolve) => {
    let url = null;
    try {
      url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { resolve(img.naturalWidth > 0 ? img : null); setTimeout(() => URL.revokeObjectURL(url), 4000); };
      img.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
      img.src = url;
    } catch { if (url) URL.revokeObjectURL(url); resolve(null); }
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    try { canvas.toBlob((b) => resolve(b || null), 'image/png'); } catch { resolve(null); }
  });
}

/**
 * Draw the domain line centred on `cx`, with its MIDDLE on `cy`.
 *
 * Measured rather than assumed: a system-font stack resolves to a different face
 * on every OS, so a nominal cap height would centre the line correctly on exactly
 * one of them. `letterSpacing` is set before measuring because it changes the
 * width — and it is simply ignored by engines that don't support it, which costs
 * a little tracking and nothing else.
 *
 * @returns {number} the line's height, for the caller's stack math.
 */
function drawSiteLine(g, text, cx, cy, size) {
  g.save();
  /* ITALIC (Ryan 2026-08-07, *"make the website italics on all"*). Set in the `font` shorthand
     rather than as a separate property because canvas has no `fontStyle` — a shorthand missing the
     style silently resets it to normal, so the order here (style, weight, size, family) is the whole
     mechanism. */
  g.font = `italic 600 ${size}px ${UI_FONT}`;
  try { g.letterSpacing = '0.06em'; } catch { /* not supported — tracking only */ }
  g.fillStyle = TEXT_INK;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const m = g.measureText(text);
  g.fillText(text, cx, cy);
  g.restore();
  const asc = m.actualBoundingBoxAscent;
  const desc = m.actualBoundingBoxDescent;
  return Number.isFinite(asc) && Number.isFinite(desc) ? asc + desc : size;
}

/**
 * Draw the Blue Sky mark as the header and report the height it consumed.
 *
 * ⚠ ONE RECIPE, TWO ARTEFACTS — the single download and the multi-product sheet wear the identical
 * header and footer, so they read as one set in an inbox. That is also why the sub-header is gone:
 * it was the only part that differed between them.
 *
 * ⚠ SIZED FROM THE WIDTH, THEN CAPPED BY A SHARE OF THE WIDTH — not by a share of the HEIGHT. The
 * two artefacts have opposite aspects (a single plate is tall and narrow, a 2-up sheet is wide and
 * short), and a height-derived cap would give them visibly different marks. Width is the dimension
 * they both present at.
 *
 * @returns {number} the block's total height in px, 0 when there is no mark to draw
 */
function drawHeaderLogo(g, cw, logo) {
  if (!logo) return 0;
  const aspect = logo.naturalWidth / logo.naturalHeight;
  let lw = cw * HEAD_LOGO_OF_WIDTH;
  let lh = lw / aspect;
  const maxH = cw * HEAD_LOGO_MAX_H_OF_WIDTH;
  if (lh > maxH) { lh = maxH; lw = lh * aspect; }
  const padTop = Math.round(lh * HEAD_PAD_TOP_OF_LOGO);
  const padBottom = Math.round(lh * HEAD_PAD_BOTTOM_OF_LOGO);
  if (g) g.drawImage(logo, Math.round(cw / 2 - lw / 2), padTop, Math.round(lw), Math.round(lh));
  return Math.round(padTop + lh + padBottom);
}

/**
 * Compose an image onto a white sheet with the Blue Sky header and footer.
 *
 * ⚠ NEVER THROWS AND NEVER RETURNS NOTHING. Attribution is worth having and it is
 * not worth a failed download, so every path that can't finish hands back the
 * ORIGINAL blob untouched.
 *
 * @param {Blob} plateBlob a transparent, alpha-cropped PNG from `portraitPNG` — or, for the contact
 *   sheet, the already-composed grid (it is opaque white, which changes nothing here: it is drawn
 *   onto a white sheet either way).
 * @param {object} [opts] reserved — the header and footer are the same on every artefact by design
 *   (see `drawHeaderLogo`), so there is deliberately nothing to pass.
 * @returns {Promise<Blob>} the composed image, or `plateBlob` if it couldn't be
 */
export async function composeCreditImage(plateBlob, opts = {}) {
  if (!plateBlob) return plateBlob;
  try {
    const [plate, logo] = await Promise.all([loadBlob(plateBlob), loadLogo()]);
    if (!plate) return plateBlob;

    const w = plate.naturalWidth;
    const h = plate.naturalHeight;
    const pad = Math.round(Math.max(w, h) * PAD_OF_LONG_EDGE);
    const cw = w + pad * 2;
    const footer = Math.max(FOOTER_MIN_PX, Math.round(cw * FOOTER_OF_WIDTH));

    /* ⚠ MEASURED BEFORE THE CANVAS EXISTS, because the header's height decides the canvas height and
       a canvas cannot be resized after drawing without clearing it. `drawHeaderLogo(null, …)` returns
       the height without drawing, so the measurement and the paint can never disagree. */
    const headerH = drawHeaderLogo(null, cw, logo);
    const ch = headerH + pad + h + footer;

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const g = canvas.getContext('2d');
    if (!g) return plateBlob;

    g.fillStyle = SHEET;
    g.fillRect(0, 0, cw, ch);
    drawHeaderLogo(g, cw, logo);
    g.drawImage(plate, pad, headerH + pad, w, h);

    const footerTop = headerH + pad + h;
    const cx = cw / 2;
    const textSize = Math.max(TEXT_MIN_PX, Math.round(cw * TEXT_OF_WIDTH));

    /* ONE LINE, CENTRED IN ITS BAND. The mark used to sit above it and now heads the sheet instead,
       so there is no stack to balance and no `logo ? … : …` fork — the no-mark path and the normal
       path became the same path, which is one fewer way for the footer to look different. */
    drawSiteLine(g, SITE_LINE, cx, footerTop + footer / 2, textSize);

    const out = await canvasToBlob(canvas);
    return out || plateBlob;
  } catch {
    return plateBlob; // a tainted canvas, a dead context, anything — still download
  }
}
